import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, getDay } from 'date-fns';
import CreateProjectModal from '../../../components/ProjectManagement/CreateProjectModal';
import CreateTaskModal from '../../../components/ProjectManagement/CreateTaskModal';

const { FiCalendar, FiEdit, FiTrash2, FiSave, FiX, FiChevronLeft, FiChevronRight, FiPlus, FiCheckSquare } = FiIcons;

const CalendarView = () => {
  const { data, updateItem, deleteItem } = useAuth0Data();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    owner: '',
    dueDate: '',
    status: 'planning'
  });

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get the day of the week for the first day of the month (0 = Sunday, 6 = Saturday)
  const startDayOfWeek = getDay(monthStart);

  const getProjectsForDate = (date) => {
    return data.projects.filter(project =>
      project.dueDate && isSameDay(parseISO(project.dueDate), date)
    );
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleEditProject = (project) => {
    setEditingProject(project.id);
    setFormData({
      title: project.title,
      description: project.description,
      owner: project.owner,
      dueDate: project.dueDate,
      status: project.status
    });
    setSelectedProject(null);
  };

  const handleSaveProject = () => {
    updateItem('projects', editingProject, formData);
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      owner: '',
      dueDate: '',
      status: 'planning'
    });
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      owner: '',
      dueDate: '',
      status: 'planning'
    });
  };

  const handleCreateSuccess = () => {
    console.log('Project created successfully!');
  };

  const handleTaskSuccess = () => {
    console.log('Task created successfully!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'on-hold': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="ml-3 text-xl font-bold text-gray-900">Project Calendar</h3>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Create Project
            </button>
            <button
              onClick={handlePrevMonth}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
            </button>
            <h4 className="text-lg font-semibold text-gray-900 min-w-48 text-center">
              {format(currentDate, 'MMMM yyyy')}
            </h4>
            <button
              onClick={handleNextMonth}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before the first of the month */}
          {Array.from({ length: startDayOfWeek }).map((_, index) => (
            <div
              key={`empty-${index}`}
              className="min-h-24 p-2 border border-gray-100 rounded-lg bg-gray-50"
            />
          ))}
          {days.map((day, index) => {
            const dayProjects = getProjectsForDate(day);
            const isCurrentDay = isToday(day);

            return (
              <motion.div
                key={day.toString()}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.01 }}
                className={`min-h-24 p-2 border border-gray-200 rounded-lg ${
                  isCurrentDay ? 'bg-primary-50 border-primary-200' : 'bg-white hover:bg-gray-50'
                } transition-colors cursor-pointer`}
              >
                <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-primary-700' : 'text-gray-700'}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1">
                  {dayProjects.slice(0, 2).map(project => (
                    <div
                      key={project.id}
                      onClick={() => setSelectedProject(project)}
                      className={`text-xs px-2 py-1 rounded border cursor-pointer hover:shadow-sm transition-shadow ${getStatusColor(project.status)}`}
                    >
                      <div className="font-medium truncate">{project.title}</div>
                    </div>
                  ))}
                  {dayProjects.length > 2 && (
                    <div className="text-xs text-gray-500 px-2">
                      +{dayProjects.length - 2} more
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Project Details Modal */}
      {selectedProject && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedProject(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">Project Details</h4>
              <button
                onClick={() => setSelectedProject(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h5 className="font-semibold text-gray-900">{selectedProject.title}</h5>
                <p className="text-sm text-gray-600 mt-1">{selectedProject.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Owner:</span>
                  <p className="font-medium text-gray-900">{selectedProject.owner}</p>
                </div>
                <div>
                  <span className="text-gray-500">Due Date:</span>
                  <p className="font-medium text-gray-900">
                    {selectedProject.dueDate ? format(parseISO(selectedProject.dueDate), 'MMM dd, yyyy') : 'Ongoing'}
                  </p>
                </div>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Status:</span>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProject.status)}`}>
                    {selectedProject.status}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Progress:</span>
                <div className="mt-1">
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-primary-500 h-2 rounded-full"
                        style={{
                          width: `${selectedProject.tasks?.length > 0 
                            ? (selectedProject.tasks.filter(t => t.status === 'completed').length / selectedProject.tasks.length) * 100 
                            : 0}%`
                        }}
                      />
                    </div>
                    <span className="text-sm text-gray-600">
                      {selectedProject.tasks?.filter(t => t.status === 'completed').length || 0}/{selectedProject.tasks?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    setShowTaskModal(selectedProject.id);
                  }}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <SafeIcon icon={FiCheckSquare} className="w-4 h-4 mr-2" />
                  Add Task
                </button>
                <button
                  onClick={() => handleEditProject(selectedProject)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiEdit} className="w-4 h-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    deleteItem('projects', selectedProject.id);
                    setSelectedProject(null);
                  }}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCancelEdit}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">Edit Project</h4>
              <button
                onClick={handleCancelEdit}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                <input
                  type="text"
                  value={formData.owner}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="planning">Planning</option>
                  <option value="in-progress">In Progress</option>
                  <option value="on-hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={handleSaveProject}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showTaskModal !== null}
        onClose={() => setShowTaskModal(null)}
        projectId={showTaskModal}
        onSuccess={handleTaskSuccess}
      />
    </div>
  );
};

export default CalendarView;